<template>
  <clinician-app-layout>
    <!-- Page Title -->
    <div class="max-w-6xl mx-auto pt-6 lg:px-8 sm:px-6">
      <h2 class="text-3xl text-vice-teal-900 font-semibold leading-tight">Shared with me</h2>
    </div>

    <div class="max-w-6xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <!-- Empty State -->
      <div v-if="sharedContent.length === 0" class="bg-white rounded-lg shadow p-12 text-center">
        <svg
          class="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-900">No shared content</h3>
        <p class="mt-1 text-sm text-gray-500">
          No recordings or snapshots have been shared with you yet.
        </p>
      </div>

      <!-- Content List -->
      <div v-else class="space-y-4">
        <div
          v-for="item in sharedContent"
          :key="`${item.type}-${item.id}`"
          class="bg-white rounded-lg shadow px-6 py-3 hover:shadow-md transition cursor-pointer"
          @click="viewContent(item)"
        >
          <div class="flex items-center justify-between">
            <div class="flex-1">
              <div class="flex items-center space-x-3 mb-1">
                <!-- Device Type -->
                <h3 class="text-lg font-medium text-vice-teal-700">
                  {{ item.device_type.replace(/_/g, ' ') }}
                </h3>

                <!-- Type Badge -->
                <span
                  :class="
                    item.type === 'recording'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-purple-100 text-purple-800'
                  "
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                >
                  {{ item.type === 'recording' ? 'Recording' : 'Snapshot' }}
                </span>
              </div>

              <p class="text-sm">
                <span class="text-gray-500 mt-2">Shared by {{ item.clinician.name }}</span>
              </p>

              <p class="text-sm text-gray-500">
                <span class="font-semibold">Session:</span>
                {{ item.session_name }}
              </p>

              <div class="flex items-center space-x-4 text-sm text-gray-500">
                <span v-if="item.duration">
                  <span class="font-semibold">Duration:</span>
                  {{ item.duration }}
                </span>
                <span v-if="item.file_size">
                  <span class="font-semibold">Size:</span>
                  {{ item.file_size }}
                </span>
                <span>
                  <span class="font-semibold">Shared:</span>
                  {{ formatDate(item.shared_at) }}
                </span>
                <span v-if="item.comment_count > 0" class="flex items-center">
                  <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                    />
                  </svg>
                  {{ item.comment_count }} {{ item.comment_count === 1 ? 'comment' : 'comments' }}
                </span>
              </div>
            </div>

            <div class="flex">
              <!-- View Button -->
              <button
                class="
                  ml-4
                  px-4
                  py-2
                  bg-vice-teal-600
                  text-white
                  font-medium
                  rounded-md
                  hover:bg-vice-teal-700
                  transition
                "
              >
                View
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </clinician-app-layout>
</template>

<script>
import ClinicianAppLayout from '@/Layouts/ClinicianAppLayout.vue';

export default {
  components: {
    ClinicianAppLayout,
  },
  props: {
    sharedContent: {
      type: Array,
      required: true,
    },
  },
  methods: {
    viewContent(item) {
      if (item.type === 'recording') {
        this.$inertia.visit(this.route('clinician.shared-recordings.show', item.id));
      } else {
        this.$inertia.visit(this.route('clinician.shared-snapshots.show', item.id));
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return 'Today';
      }
      if (diffDays === 1) {
        return 'Yesterday';
      }
      if (diffDays < 7) {
        return `${diffDays} days ago`;
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
  },
};
</script>
